import React from 'react';
import { Box, Tabs, Tab, Paper } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  EmojiObjects as RecommendIcon,
  Troubleshoot as TroubleshootIcon,
  MouseOutlined as MouseOutlinedIcon,
  Lightbulb as LightbulbIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { RecommendationsPanel } from './RecommendationsPanel';
import { StrikingDistanceDashboard } from './StrikingDistanceDashboard';
import { CtrAnomalyDashboard } from './CtrAnomalyDashboard';
import { IntentGapDashboard } from './IntentGapDashboard';
import { CannibalizationDashboard } from './CannibalizationDashboard';

const TABS = [
  {
    label: 'Recommendations',
    path: '/opportunities',
    icon: <RecommendIcon fontSize="small" />,
  },
  {
    label: 'Striking Distance',
    path: '/opportunities/striking-distance',
    icon: <TroubleshootIcon fontSize="small" />,
  },
  {
    label: 'CTR Anomaly',
    path: '/opportunities/ctr-anomaly',
    icon: <MouseOutlinedIcon fontSize="small" />,
  },
  {
    label: 'Intent Gap',
    path: '/opportunities/intent-gaps',
    icon: <LightbulbIcon fontSize="small" />,
  },
  {
    label: 'Cannibalization',
    path: '/opportunities/cannibalization',
    icon: <ContentCopyIcon fontSize="small" />,
  },
];

/**
 * OpportunitiesHub
 *
 * Single Opportunities page with 4 signal-based tabs:
 *   - Striking Distance  : GA4 + GSC ROI opportunities
 *   - CTR Anomaly        : Pages whose CTR is below position-expected rate
 *   - Intent Gap         : Queries revealing unmet search intent
 *   - Cannibalization    : Competing pages fragmenting the same query
 *
 * URL mapping (tabs are deep-linkable):
 *   /opportunities                  → Striking Distance tab
 *   /opportunities/ctr-anomaly      → CTR Anomaly tab
 *   /opportunities/intent-gaps      → Intent Gap tab
 *   /opportunities/cannibalization  → Cannibalization tab
 */
export const OpportunitiesHub: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Derive active tab from current URL path
  const activeIdx = TABS.reduce((found, tab, idx) => {
    if (tab.path === '/opportunities') {
      // Exact match only (avoid matching /opportunities/something)
      return pathname === '/opportunities' || pathname === '/opportunities/'
        ? idx
        : found;
    }
    return pathname.startsWith(tab.path) ? idx : found;
  }, 0);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    navigate(TABS[newValue].path);
  };

  return (
    <Box>
      {/* Tab bar — tabs act as the primary navigation for this section */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Tabs
          value={activeIdx}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.path}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab panels — lazy-rendered; each dashboard is fully self-contained */}
      {activeIdx === 0 && <RecommendationsPanel />}
      {activeIdx === 1 && <StrikingDistanceDashboard />}
      {activeIdx === 2 && <CtrAnomalyDashboard />}
      {activeIdx === 3 && <IntentGapDashboard />}
      {activeIdx === 4 && <CannibalizationDashboard />}
    </Box>
  );
};
